// 必要なモジュールを読み込み
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";

// Post Processing
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { DotShaderPass } from "./postprocessing/DotShaderPass";

// Core
import { createScene } from "./core/core";
import { createRenderer } from "./core/core";
import { createCamera } from "./core/core";
import { createSunLight, createAmbientLight } from "./world/light";

// Models
import { yoguru } from "./world/yoguru";

export class App {
  // Elements
  private assetsSrc: string;

  // Canvas
  private canvas: HTMLCanvasElement;
  private canvasWidth: number;
  private canvasHeight: number;
  private canvasPixelRatio: number;

  // Three Init
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private controls!: OrbitControls;

  // Post Processing
  private composer!: EffectComposer;
  private dotShaderPass!: DotShaderPass;

  // Lights
  private sunLight!: THREE.DirectionalLight;
  private ambientLight!: THREE.AmbientLight;

  //objects
  private yoguruModel!: yoguru;

  // clock
  private clock = new THREE.Clock();

  constructor(canvas: HTMLCanvasElement) {
    // canvas
    this.canvas = canvas;
    this.canvasWidth = canvas.getBoundingClientRect().width;
    this.canvasHeight = canvas.getBoundingClientRect().height;
    this.canvasPixelRatio = Math.min(window.devicePixelRatio, 2);

    // Three.js setup
    this.scene = createScene();
    this.scene.background = null; // 透明に設定

    //camera
    this.camera = createCamera(this.canvasWidth / this.canvasHeight);

    //lights
    this.sunLight = createSunLight();
    this.scene.add(this.sunLight);
    this.ambientLight = createAmbientLight();
    this.scene.add(this.ambientLight);

    //objects
    this.assetsSrc = this.canvas.dataset.assetsSrc ?? "";
    this.yoguruModel = new yoguru(this.scene, this.assetsSrc);

    //renderer
    this.renderer = createRenderer(canvas);
    this.renderer.setSize(this.canvasWidth, this.canvasHeight, false);
    this.renderer.setPixelRatio(this.canvasPixelRatio);

    // OrbitControls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.enableZoom = false;

    // Post Processing Setup
    this.composer = new EffectComposer(this.renderer);
    const renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(renderPass);

    // ドット絵風エフェクト
    this.dotShaderPass = new DotShaderPass();
    this.dotShaderPass.setResolution(this.canvasWidth, this.canvasHeight);
    this.dotShaderPass.setPixelSize(2.0);
    this.dotShaderPass.setColorLevels(20.0);
    this.composer.addPass(this.dotShaderPass);

    // リサイズ処理
    window.addEventListener("resize", () => {
      this.handleResize();
    });
  }

  private handleResize(): void {
    // キャンバスサイズを更新
    this.canvasWidth = this.canvas.getBoundingClientRect().width;
    this.canvasHeight = this.canvas.getBoundingClientRect().height;

    // カメラのアスペクト比を更新
    this.camera.aspect = this.canvasWidth / this.canvasHeight;
    this.camera.updateProjectionMatrix();

    // レンダラーのサイズを更新
    this.renderer.setSize(this.canvasWidth, this.canvasHeight, false);

    // Post Processingのサイズを更新
    this.composer.setSize(this.canvasWidth, this.canvasHeight);
    this.dotShaderPass.setResolution(this.canvasWidth, this.canvasHeight);
  }

  /**
   * 初期化処理
   */
  init() {
    // レンダリングループを開始
    this.render();
  }

  public render = (): void => {
    requestAnimationFrame(this.render);

    // アニメーション更新
    const deltaTime = this.clock.getDelta();
    if (deltaTime > 0) {
      this.yoguruModel.update(deltaTime);
    }
    this.controls.update();

    // Post Processingでレンダリング
    this.composer.render();
  };
}
